import { h } from 'preact'
import { useMemo, useState, useCallback } from 'preact/hooks'
import PropTypes from 'prop-types'

import classNames from 'lib/classNames'
import {
  getLayoutMode,
  getGridTemplate,
  paginateParticipants,
  LAYOUT_SOLO,
  LAYOUT_PIP,
  LAYOUT_GRID,
  LAYOUT_CAROUSEL,
  LAYOUT_SCREEN_SHARE,
  MAX_PER_PAGE,
} from 'lib/layoutEngine'

import ParticipantTile from './ParticipantTile'
import DraggablePIP from './DraggablePIP'
import AudioPlayer from './AudioPlayer'

export default function ParticipantGrid({
  localVideoTrack,
  localAudioTrack,
  localScreenTrack,
  localIdentity,
  isMicMuted,
  isVideoEnabled,
  isScreenSharing,
  participants,
  activeSpeakerIdentity,
  // Admin features
  isAdmin,
  onKickParticipant,
  onMuteParticipant,
}) {
  const [carouselPage, setCarouselPage] = useState(0)

  const localParticipant = useMemo(() => ({
    identity: localIdentity || 'You',
    name: 'You',
    isMuted: isMicMuted,
    isVideoEnabled,
    isScreenSharing,
    videoTrack: localVideoTrack,
    audioTrack: localAudioTrack,
    screenTrack: localScreenTrack || null,
    connectionQuality: 'excellent',
    isLocal: true,
  }), [localIdentity, isMicMuted, isVideoEnabled, isScreenSharing, localVideoTrack, localAudioTrack, localScreenTrack])

  const activeScreenShare = useMemo(() => {
    if (localParticipant.isScreenSharing && localParticipant.screenTrack) {
      return localParticipant
    }
    for (let i = 0; i < participants.length; i++) {
      const p = participants[i]
      if (p.isScreenSharing && p.screenTrack) return p
    }
    return null
  }, [localParticipant, participants])

  const totalCount = 1 + participants.length
  const hasScreen = !!activeScreenShare

  const activeSpeaker = useMemo(() => {
    if (activeSpeakerIdentity) return activeSpeakerIdentity
    const speaking = participants.find(p => p.isSpeaking)
    return speaking ? speaking.identity : null
  }, [activeSpeakerIdentity, participants])

  const layoutMode = getLayoutMode(totalCount, hasScreen)

  // All participants combined (local first) for grid/carousel
  const allParticipants = useMemo(() => {
    return [localParticipant, ...participants]
  }, [localParticipant, participants])

  // Carousel pages
  const pages = useMemo(() => {
    if (layoutMode !== LAYOUT_CAROUSEL) return [allParticipants]
    return paginateParticipants(allParticipants, activeSpeaker, MAX_PER_PAGE)
  }, [layoutMode, allParticipants, activeSpeaker])

  // Reset carousel page if out of bounds
  const safePage = Math.min(carouselPage, pages.length - 1)
  if (safePage !== carouselPage) setCarouselPage(safePage)

  const handlePrevPage = useCallback(() => {
    setCarouselPage(p => Math.max(0, p - 1))
  }, [])

  const handleNextPage = useCallback(() => {
    setCarouselPage(p => Math.min(pages.length - 1, p + 1))
  }, [pages.length])

  const gridTemplate = layoutMode === LAYOUT_GRID ? getGridTemplate(totalCount) : null

  const screenLabel = (p) => {
    const ownerName = p.identity === (localIdentity || 'You')
      ? 'Your'
      : (p.name || p.identity) + "'s"
    return ownerName + ' Screen'
  }

  const gridClass = classNames('ParticipantGrid', {
    layoutSolo: layoutMode === LAYOUT_SOLO,
    layoutPip: layoutMode === LAYOUT_PIP,
    layoutGrid: layoutMode === LAYOUT_GRID,
    layoutCarousel: layoutMode === LAYOUT_CAROUSEL,
    layoutScreenShare: layoutMode === LAYOUT_SCREEN_SHARE,
  })

  // ---------- RENDER HELPERS ----------

  const renderTile = (p, isLocalUser) => (
    <ParticipantTile
      key={p.identity}
      participant={p}
      isLocal={isLocalUser}
      isSpeaker={activeSpeaker === p.identity}
      isAdmin={isAdmin}
      onKick={!isLocalUser && isAdmin ? onKickParticipant : undefined}
      onMute={!isLocalUser && isAdmin ? onMuteParticipant : undefined}
    />
  )

  // ---------- SOLO LAYOUT ----------
  const renderSolo = () => (
    <div className="ParticipantGrid-soloView">
      {renderTile(localParticipant, true)}
    </div>
  )

  // ---------- PIP LAYOUT ----------
  const renderPIP = () => {
    const remoteParticipant = participants[0]
    if (!remoteParticipant) return renderSolo()
    return (
      <div className="ParticipantGrid-pipView">
        <div className="ParticipantGrid-pipMain">
          {renderTile(remoteParticipant, false)}
        </div>
        <DraggablePIP
          participant={localParticipant}
          isLocal
          isSpeaker={activeSpeaker === localParticipant.identity}
        />
      </div>
    )
  }

  // ---------- GRID LAYOUT ----------
  const renderGrid = () => {
    const style = gridTemplate
      ? {
        gridTemplateColumns: `repeat(${gridTemplate.columns}, minmax(0, 1fr))`,
        gridTemplateRows: `repeat(${gridTemplate.rows}, minmax(0, 1fr))`,
      }
      : {}
    return (
      <div className="ParticipantGrid-gridView" style={style}>
        {renderTile(localParticipant, true)}
        {participants.map(p => renderTile(p, false))}
      </div>
    )
  }

  // ---------- CAROUSEL LAYOUT ----------
  const renderCarousel = () => {
    const currentPage = pages[safePage] || []
    const gridTpl = getGridTemplate(Math.min(currentPage.length, MAX_PER_PAGE))
    const style = {
      gridTemplateColumns: `repeat(${gridTpl.columns}, minmax(0, 1fr))`,
      gridTemplateRows: `repeat(${gridTpl.rows}, minmax(0, 1fr))`,
    }
    return (
      <div className="ParticipantGrid-carouselView">
        <div className="ParticipantGrid-carouselGrid" style={style}>
          {currentPage.map(p => renderTile(p, p.identity === localParticipant.identity))}
        </div>
        {pages.length > 1 && (
          <div className="ParticipantGrid-carouselNav">
            <button
              className="ParticipantGrid-carouselBtn"
              onClick={handlePrevPage}
              disabled={safePage === 0}
              title="Previous page"
            >
              ‹
            </button>
            <span className="ParticipantGrid-carouselIndicator">
              {safePage + 1} / {pages.length}
            </span>
            <button
              className="ParticipantGrid-carouselBtn"
              onClick={handleNextPage}
              disabled={safePage === pages.length - 1}
              title="Next page"
            >
              ›
            </button>
          </div>
        )}
      </div>
    )
  }

  // ---------- SCREEN SHARE LAYOUT ----------
  const renderScreenShare = () => (
    <div className="ParticipantGrid-screenShareView">
      <div className="ParticipantGrid-screenArea">
        <div className="ParticipantGrid-screenMain">
          <ParticipantTile
            participant={{
              ...activeScreenShare,
              videoTrack: activeScreenShare.screenTrack,
              isVideoEnabled: true,
              name: screenLabel(activeScreenShare),
            }}
            isScreenShare
          />
        </div>
      </div>
      <div className="ParticipantGrid-filmstrip">
        {renderTile(localParticipant, true)}
        {participants.map(p => renderTile(p, false))}
      </div>
    </div>
  )

  // ---------- MAIN RENDER ----------
  const renderLayout = () => {
    switch (layoutMode) {
    case LAYOUT_SOLO: return renderSolo()
    case LAYOUT_PIP: return renderPIP()
    case LAYOUT_GRID: return renderGrid()
    case LAYOUT_CAROUSEL: return renderCarousel()
    case LAYOUT_SCREEN_SHARE: return renderScreenShare()
    default: return renderGrid()
    }
  }

  return (
    <div className={gridClass}>
      {renderLayout()}

      {/* Audio players for remote participants (always rendered) */}
      {participants.map(p =>
        p.audioTrack
          ? <AudioPlayer key={'audio-' + p.identity} track={p.audioTrack} />
          : null
      )}
    </div>
  )
}

ParticipantGrid.propTypes = {
  localVideoTrack: PropTypes.object,
  localAudioTrack: PropTypes.object,
  localScreenTrack: PropTypes.object,
  localIdentity: PropTypes.string,
  isMicMuted: PropTypes.bool,
  isVideoEnabled: PropTypes.bool,
  isScreenSharing: PropTypes.bool,
  participants: PropTypes.array.isRequired,
  activeSpeakerIdentity: PropTypes.string,
  isAdmin: PropTypes.bool,
  onKickParticipant: PropTypes.func,
  onMuteParticipant: PropTypes.func,
}
